import React, { useEffect, useState, useCallback, useRef } from 'react';
import Navbar from '../../Company/About/Navbar';
import WelcomeSection from './WelcomeSection';
import TestCard from './TestCard';
import UpcomingSchedule from './UpcomingSchedule';
import { Container } from '@/components/ui/layout';
import { normalizeTestType, getTestsForCategory } from '../utils/testCategories';
import { isTestExpired, isTestAvailableNow } from '../utils/testTimeUtils';

// Auto-refresh interval in milliseconds (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;
// UI update interval for countdown timers and button state (without API calls)
const UI_UPDATE_INTERVAL = 1000;

export default function DashboardLayout() {
  const [userName, setUserName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [experience, setExperience] = useState(null);
  const [tests, setTests] = useState([]);
  const [selectedTestType, setSelectedTestType] = useState("t");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Refs to store interval IDs for cleanup
  const refreshIntervalRef = useRef(null);
  const uiUpdateIntervalRef = useRef(null);

  const fetchTests = useCallback(async () => {
    try {
      const response = await fetch(`/api/get_test_details`, {
        method: "POST"
      });
      if (!response.ok) {
        throw new Error('Error fetching tests');
      }
      const data = await response.json();
      setTests(data || []);
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchTests();

    fetch('/api/user')
      .then(response => response.json())
      .then(data => {
        setUserName(data.user_name);
        setExperience(data.experience);
        setLocation(data.location);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });

    // Set up periodic refresh for test data (30 seconds)
    refreshIntervalRef.current = setInterval(() => {
      fetchTests();
    }, AUTO_REFRESH_INTERVAL);

    // Set up UI update interval for countdown timers and button state (1 second)
    // This updates the UI without making API calls - triggers re-renders so
    // isTestAvailableNow() and countdown timers recalculate with current time
    uiUpdateIntervalRef.current = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, UI_UPDATE_INTERVAL);

    // Cleanup intervals on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (uiUpdateIntervalRef.current) {
        clearInterval(uiUpdateIntervalRef.current);
      }
    };
  }, [fetchTests]);

  // Helper function to get test status (same logic as in TestCard)
  const getTestStatus = (test) => {
    const userEmail = localStorage.getItem("user_email");
    const isPrivilegedUser = userEmail === "anubhav.mishra@newzenalpha.com" || userEmail === "anubhav.mishra303@gmail.com";
    if (isPrivilegedUser) return "upcoming";
    if (test.test_status === "in_progress") return "in_progress";
    if (test.test_status === "completed") return "completed";
    if (isTestExpired(test.scheduled_time)) return "expired";
    if (isTestAvailableNow(test.scheduled_time)) return "upcoming";
    return "pending";
  };

  const safeTests = Array.isArray(tests) ? tests : [];
  const categoryFilteredTests = getTestsForCategory(safeTests, selectedTestType);
  
  // Apply status filter
  const filterTests = selectedStatus === "all" 
    ? categoryFilteredTests 
    : categoryFilteredTests.filter(test => getTestStatus(test) === selectedStatus);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-muted/30 flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <WelcomeSection userName={userName} location={location} experience={experience} tests={tests} />
        
        {/* Main Content */}
        <section className="py-6">
          <Container size="2xl">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
              {/* Test Cards - Main Content */}
              <TestCard 
                tests={filterTests} 
                allTests={safeTests}
                setSelectedTestType={setSelectedTestType}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
              />
              
              {/* Upcoming Schedule - Sidebar */}
              <aside className="xl:sticky xl:top-4 xl:self-start">
                <UpcomingSchedule tests={tests} />
              </aside>
            </div>
          </Container>
        </section>
      </main>
    </div>
  );
}
